import { ChevronRight, Focus, X } from "lucide-react";
import { useEffect, useState } from "react";
import type GicsSubIndustryDetail from "@/_generated/types/serializers/GicsSubIndustryDetail.ts";
import { bizminer_admin_industry_path } from "@/routes";
import { type GicsLevel, LEVEL_CONFIG } from "../types.ts";

interface PanelHeaderProps {
  nodeData: GicsSubIndustryDetail | null;
  nodeId: string;
  onClose: () => void;
  onFocus?: (ancestorIds: string[], nodeId: string) => void;
}

const PanelHeader = ({
  nodeData,
  nodeId,
  onClose,
  onFocus,
}: PanelHeaderProps) => {
  const levelConfig = LEVEL_CONFIG[4];

  return (
    <div className="flex items-start justify-between gap-2 p-4 border-b border-border">
      <div className="min-w-0 flex-1">
        {nodeData && (
          <>
            <div className="flex items-center gap-2 mb-1">
              <span
                className={`px-1.5 py-0.5 rounded text-xs font-medium ${levelConfig.bgClass} ${levelConfig.textClass}`}
              >
                {levelConfig.badge}
              </span>
              <span className="text-xs text-muted-foreground">
                {levelConfig.label}
              </span>
            </div>
            <h3 className="font-semibold text-card-foreground">
              {nodeData.name}
            </h3>
            <p className="text-xs text-muted-foreground mt-1">
              Code: {nodeData.code}
            </p>
          </>
        )}
      </div>
      <div className="flex items-center gap-1">
        {nodeData && (
          <button
            type="button"
            onClick={() => onFocus?.(nodeData.ancestor_ids, nodeId)}
            className="shrink-0 p-1 rounded hover:bg-accent text-muted-foreground hover:text-foreground"
            title="Focus on node in tree"
          >
            <Focus className="h-4 w-4" />
          </button>
        )}
        <button
          type="button"
          onClick={onClose}
          className="shrink-0 p-1 rounded hover:bg-accent text-muted-foreground hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

interface AncestorPathProps {
  ancestors: GicsSubIndustryDetail["ancestors"];
}

const AncestorPath = ({ ancestors }: AncestorPathProps) => {
  if (ancestors.length === 0) return null;

  return (
    <div>
      <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
        Path
      </h4>
      <div className="flex flex-col gap-1">
        {ancestors.map((ancestor, index) => (
          <div key={ancestor.id} className="flex items-center">
            {index > 0 && (
              <ChevronRight className="h-3 w-3 text-muted-foreground mr-1" />
            )}
            <span
              className={`px-1.5 py-0.5 rounded text-xs font-medium ${LEVEL_CONFIG[ancestor.level as GicsLevel].bgClass} ${LEVEL_CONFIG[ancestor.level as GicsLevel].textClass}`}
            >
              {ancestor.name}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

interface DescriptionSectionProps {
  description: string | null;
}

const DescriptionSection = ({ description }: DescriptionSectionProps) => {
  if (!description) {
    return (
      <div className="text-sm text-muted-foreground italic">
        No description available
      </div>
    );
  }

  return (
    <div>
      <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
        Description
      </h4>
      <p className="text-sm text-card-foreground leading-relaxed">
        {description}
      </p>
    </div>
  );
};

interface Props {
  nodeId: string | null;
  onClose: () => void;
  onFocus?: (ancestorIds: string[], nodeId: string) => void;
}

export default function SubIndustryDetailPanel({
  nodeId,
  onClose,
  onFocus,
}: Props) {
  const [nodeData, setNodeData] = useState<GicsSubIndustryDetail | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!nodeId) {
      setNodeData(null);
      return;
    }

    const fetchNodeData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await fetch(bizminer_admin_industry_path(nodeId));
        if (response.ok) {
          const data: GicsSubIndustryDetail = await response.json();
          setNodeData(data);
        } else {
          setError("Failed to load sub-industry details");
        }
      } catch (_err) {
        setError("Failed to load sub-industry details");
      } finally {
        setIsLoading(false);
      }
    };

    fetchNodeData();
  }, [nodeId]);

  if (!nodeId) return null;

  return (
    <div className="absolute top-0 right-0 w-80 border-l border-border bg-card flex flex-col h-full shadow-lg z-10">
      <PanelHeader
        nodeData={nodeData}
        nodeId={nodeId}
        onClose={onClose}
        onFocus={onFocus}
      />

      <div className="flex-1 overflow-auto p-4">
        {isLoading && (
          <div className="text-sm text-muted-foreground">Loading...</div>
        )}

        {error && <div className="text-sm text-destructive">{error}</div>}

        {nodeData && !isLoading && (
          <div className="space-y-6">
            <AncestorPath ancestors={nodeData.ancestors} />
            <DescriptionSection description={nodeData.description} />
          </div>
        )}
      </div>
    </div>
  );
}
